import { createFileRoute, redirect } from '@tanstack/react-router';
import {
  BankTransactionStatusFilter,
  BankTransactionSignFilter,
} from '@signalvote/shared';
import type { StatusPivot } from '@/components/finance/transactions/StatusPivotChips';

// CPH-003: Transactions retires as a top-level surface; this file is the
// holding redirect to `/admin/finance/close` so existing bookmarks don't
// 404. Post-USI-005 (ADR-011) the close hub collapsed to a single page.
//
// `validateSearch` survives so existing pre-CPH bookmarks that include
// query params don't throw before the `beforeLoad` redirect fires.
const VALID_PIVOTS: StatusPivot[] = ['unattributed', 'staged', 'reported', 'notreported', 'all'];

export const Route = createFileRoute('/admin/finance/transactions')({
  validateSearch: (search: Record<string, unknown>): {
    status?: StatusPivot;
    view?: 'match' | 'balance' | 'history';
    attribution?: BankTransactionStatusFilter;
    account?: string;
    sign?: BankTransactionSignFilter;
    dateFrom?: string;
    dateTo?: string;
    search?: string;
    page?: number;
    deposit?: string;
    processorId?: string;
    action?: 'refund';
    contributionId?: string;
    expenditureId?: string;
    returnTo?: 'report';
    reportId?: string;
  } => ({
    status: parseStatus(search.status),
    view: parseView(search.view),
    attribution: parseAttribution(search.attribution),
    account: typeof search.account === 'string' ? search.account : undefined,
    sign: parseSign(search.sign),
    dateFrom: typeof search.dateFrom === 'string' ? search.dateFrom : undefined,
    dateTo: typeof search.dateTo === 'string' ? search.dateTo : undefined,
    search: typeof search.search === 'string' ? search.search : undefined,
    page: typeof search.page === 'number' && search.page > 0 ? search.page : undefined,
    deposit: typeof search.deposit === 'string' ? search.deposit : undefined,
    processorId: typeof search.processorId === 'string' ? search.processorId : undefined,
    action: search.action === 'refund' ? 'refund' : undefined,
    contributionId: typeof search.contributionId === 'string' ? search.contributionId : undefined,
    expenditureId: typeof search.expenditureId === 'string' ? search.expenditureId : undefined,
    returnTo: search.returnTo === 'report' ? 'report' : undefined,
    reportId: typeof search.reportId === 'string' ? search.reportId : undefined,
  }),
  beforeLoad: () => {
    throw redirect({ to: '/admin/finance/close' });
  },
});

function parseStatus(value: unknown): StatusPivot {
  if (typeof value !== 'string') return 'unattributed';
  return (VALID_PIVOTS as string[]).includes(value)
    ? (value as StatusPivot)
    : 'unattributed';
}

function parseView(value: unknown): 'match' | 'balance' | 'history' {
  if (value === 'balance' || value === 'history') return value;
  return 'match';
}

function parseSign(value: unknown): BankTransactionSignFilter | undefined {
  if (typeof value !== 'string') return undefined;
  return Object.values(BankTransactionSignFilter).includes(value as BankTransactionSignFilter)
    ? (value as BankTransactionSignFilter)
    : undefined;
}

function parseAttribution(value: unknown): BankTransactionStatusFilter | undefined {
  if (typeof value !== 'string') return undefined;
  return Object.values(BankTransactionStatusFilter).includes(value as BankTransactionStatusFilter)
    ? (value as BankTransactionStatusFilter)
    : undefined;
}
